<script setup lang="ts">
import BaseButton from './base/BaseButton.vue';
import useResponsive from '@/utils/responsive-util';
import { paths } from '@/utils/paths';
import { getLabel } from '@/utils/data-display-utils';
import { Locales } from '@/store/language.interface';

// CONST
const { isMobile } = useResponsive();

// PROPS
const props = defineProps<{
  index: number;
  hasResponse?: boolean;
  isVoting: boolean;
  showSubmit: boolean;
  showResult: boolean;
  isFinalQuestion: boolean;
  showQuestionResults: boolean;
  selectedResponses: any;
  formType: string;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'submit'): void;
  (e: 'nextForm', index: number): void;
  (e: 'resetResponse'): void;
  (e: 'undoCancelVote'): void;
}>();

// FUNCTIONS
function submit() {
  emit('submit');
}

function resetResponse() {
  emit('resetResponse');
}

function scrollNextQuestion() {
  emit('nextForm', props.index);
}

function undoCancelVote() {
  emit('undoCancelVote');
}
</script>

<template>
  <div class="form-buttons-container" v-if="!showQuestionResults && (showSubmit || showResult)">
    <div
      v-if="isVoting && props.selectedResponses.length === 0 && !props.hasResponse && !showSubmit"
      class="form-buttons-container__undo-cancel"
    >
      <BaseButton
        class="secondary"
        :text="getLabel('buttons.cancel', props.languageEnabled)"
        fullWidth
        @click="undoCancelVote"
      ></BaseButton>
    </div>
    <div v-if="showSubmit && isVoting" class="form-buttons-container__question">
      <BaseButton
        v-if="props.formType != 'open'"
        class="secondary"
        :text="getLabel('buttons.cancel', props.languageEnabled)"
        fullWidth
        @click="resetResponse"
      ></BaseButton>
      <BaseButton
        class="action"
        :text="getLabel('buttons.submit', props.languageEnabled)"
        fullWidth
        @click="submit"
      ></BaseButton>
    </div>
    <div v-if="showResult && !isVoting" class="form-buttons-container__poll">
      <div class="form-buttons-container__poll-stats">
        <BaseButton
          v-if="!isMobile"
          class="secondary"
          left-icon="undo"
          :text="getLabel('buttons.change-vote', props.languageEnabled)"
          @click="resetResponse"
        ></BaseButton>

        <BaseButton v-else class="secondary" left-icon="undo" @click="resetResponse"></BaseButton>
      </div>
      <div class="form-buttons-container__poll-next" v-if="!props.isFinalQuestion">
        <BaseButton
          class="action"
          :fullWidth="true"
          icon="navigate_next"
          :text="getLabel('buttons.next', props.languageEnabled)"
          @click="scrollNextQuestion"
        ></BaseButton>
      </div>
      <div class="form-buttons-container__poll-thanks" v-else>
        <BaseButton
          class="action"
          :fullWidth="true"
          :text="getLabel('consultation.thank-you', props.languageEnabled)"
          :to="paths.root"
        ></BaseButton>
      </div>
    </div>
  </div>

  <div class="form-buttons-container" v-else-if="showQuestionResults">
    <div class="form-buttons-container__poll-next" v-if="!props.isFinalQuestion">
      <BaseButton
        class="action"
        icon="navigate_next"
        :text="getLabel('buttons.next', props.languageEnabled)"
        @click="scrollNextQuestion"
      ></BaseButton>
    </div>
    <div class="form-buttons-container__poll-thanks" v-else>
      <BaseButton
        class="action"
        :text="getLabel('consultation.thank-you', props.languageEnabled)"
        :to="paths.root"
      ></BaseButton>
    </div>
  </div>
</template>

<style lang="scss">
.form-buttons-container {
  display: flex;
  margin: $space-sm 0;
  gap: 5px;

  &__question {
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: $space-sm;

    .q-btn {
      width: 100%;
    }
  }

  &__undo-cancel {
    width: 100%;

    .q-btn {
      width: 100%;
    }
  }

  &__results {
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 5px;
  }

  &__poll {
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 5px;

    &-stats,
    &-next,
    &-thanks {
      width: 100%;
      height: 100%;

      .q-btn {
        width: 100%;
        height: 100%;
      }
    }
  }

  &__cancel {
    display: flex;
    width: 100%;
  }
}
</style>
